import { cluesFromLine } from './clues.js';
import type { PuzzleClues, CellValidationResult, CellError } from './types.js';

/**
 * Validate filling a single cell against the solution and clues.
 * Collects ALL applicable reasons why the cell should be empty,
 * then combines them into a human-readable message.
 */
export function validateCell(
  row: number,
  col: number,
  solution: boolean[][],
  clues: PuzzleClues,
): CellValidationResult {
  if (solution[row]?.[col]) {
    return { valid: true, errors: [], message: null };
  }

  const rowClue = clues.rows[row];
  const colClue = clues.cols[col];
  const rowTotal = rowClue.reduce((a, b) => a + b, 0);
  const colTotal = colClue.reduce((a, b) => a + b, 0);

  const errors: CellError[] = [];

  // Row has 0 filled cells
  if (rowTotal === 0) {
    errors.push({ type: 'row_empty', row });
  }

  // Column has 0 filled cells
  if (colTotal === 0) {
    errors.push({ type: 'col_empty', col });
  }

  // Row adjacency / gap checks (only if row has some filled cells)
  if (rowTotal > 0) {
    const leftFilled = col > 0 && solution[row][col - 1];
    const rightFilled = col < solution[row].length - 1 && solution[row][col + 1];
    if (!leftFilled && !rightFilled) {
      if (rowClue.length > 1) {
        errors.push({ type: 'row_gap', row });
      } else {
        errors.push({ type: 'row_no_group', row });
      }
    }
  }

  // Column adjacency / gap checks (only if column has some filled cells)
  if (colTotal > 0) {
    const aboveFilled = row > 0 && solution[row - 1][col];
    const belowFilled = row < solution.length - 1 && solution[row + 1][col];
    if (!aboveFilled && !belowFilled) {
      if (colClue.length > 1) {
        errors.push({ type: 'col_gap', col });
      } else {
        errors.push({ type: 'col_no_group', col });
      }
    }
  }

  // Row clue would be exceeded
  if (rowTotal > 0 && rowTotal < solution[row].length) {
    // Only add if we haven't already identified this row as empty or isolated
    if (!errors.some((e) => e.type === 'row_empty' || e.type === 'row_no_group' || e.type === 'row_gap')) {
      errors.push({ type: 'row_full', row, clueTotal: rowTotal });
    }
  }

  // Column clue would be exceeded
  if (colTotal > 0 && colTotal < solution.length) {
    if (!errors.some((e) => e.type === 'col_empty' || e.type === 'col_no_group' || e.type === 'col_gap')) {
      errors.push({ type: 'col_full', col, clueTotal: colTotal });
    }
  }

  // Fallback: if no specific error was identified, push a generic row_full
  if (errors.length === 0) {
    errors.push({ type: 'row_full', row, clueTotal: rowTotal });
  }

  const message = buildMessage(errors, clues);
  return { valid: false, errors, message };
}

/** Build a human-readable message from all errors. */
function buildMessage(errors: CellError[], clues: PuzzleClues): string {
  if (errors.length === 1) return describeError(errors[0], clues);

  // Try hand-crafted combo message for 2-error pairs
  if (errors.length === 2) {
    const combo = comboMessage(errors[0], errors[1], clues);
    if (combo) return combo;
  }

  // Fallback: bulleted list for 3+ or unmatched pairs
  const parts = errors.map((e) => describeError(e, clues));
  if (parts.length === 2) {
    return `${parts[0]}, and ${parts[1][0].toLowerCase()}${parts[1].slice(1)}`;
  }
  return `Multiple issues:\n${parts.map((p) => `- ${p}`).join('\n')}`;
}

/** Hand-crafted messages for common 2-error combos. */
function comboMessage(a: CellError, b: CellError, clues: PuzzleClues): string | null {
  const key = `${a.type}+${b.type}`;
  const rowErr = a.type.startsWith('row_') ? a : b;
  const colErr = a.type.startsWith('col_') ? a : b;
  const r = 'row' in rowErr ? rowErr.row + 1 : 0;
  const c = 'col' in colErr ? colErr.col + 1 : 0;
  const rowClueStr = 'row' in rowErr ? clues.rows[rowErr.row].join(' ') : '';
  const colClueStr = 'col' in colErr ? clues.cols[colErr.col].join(' ') : '';

  switch (key) {
    // Both empty
    case 'row_empty+col_empty':
      return `Row ${r} and column ${c} both have no filled cells!`;

    // Both no group
    case 'row_no_group+col_no_group':
      return `This cell doesn't belong to any group in row ${r} or column ${c}!`;

    // Both gaps
    case 'row_gap+col_gap':
      return `Row ${r} has clue ${rowClueStr} and column ${c} has clue ${colClueStr} — this cell falls in a gap in both!`;

    // Both full
    case 'row_full+col_full': {
      const rt = (rowErr as { clueTotal: number }).clueTotal;
      const ct = (colErr as { clueTotal: number }).clueTotal;
      return `Row ${r} only needs ${rt} cell${rt > 1 ? 's' : ''} and column ${c} only needs ${ct}!`;
    }

    // Empty + no group
    case 'row_empty+col_no_group':
      return `Row ${r} has no filled cells, and this cell doesn't belong to any group in column ${c}!`;
    case 'col_empty+row_no_group':
      return `Column ${c} has no filled cells, and this cell doesn't belong to any group in row ${r}!`;

    // Empty + gap
    case 'row_empty+col_gap':
      return `Row ${r} has no filled cells, and column ${c} has clue ${colClueStr} — this cell falls in a gap!`;
    case 'col_empty+row_gap':
      return `Column ${c} has no filled cells, and row ${r} has clue ${rowClueStr} — this cell falls in a gap!`;

    // Empty + full
    case 'row_empty+col_full': {
      const ct = (colErr as { clueTotal: number }).clueTotal;
      return `Row ${r} has no filled cells, and column ${c} only needs ${ct} cell${ct > 1 ? 's' : ''}!`;
    }
    case 'col_empty+row_full': {
      const rt = (rowErr as { clueTotal: number }).clueTotal;
      return `Column ${c} has no filled cells, and row ${r} only needs ${rt} cell${rt > 1 ? 's' : ''}!`;
    }

    // No group + gap
    case 'row_no_group+col_gap':
      return `This cell doesn't belong to any group in row ${r}, and column ${c} has clue ${colClueStr} — it falls in a gap!`;
    case 'row_gap+col_no_group':
      return `Row ${r} has clue ${rowClueStr} — this cell falls in a gap, and it doesn't belong to any group in column ${c}!`;

    // No group + full
    case 'row_no_group+col_full': {
      const ct = (colErr as { clueTotal: number }).clueTotal;
      return `This cell doesn't belong to any group in row ${r}, and column ${c} only needs ${ct} cell${ct > 1 ? 's' : ''}!`;
    }
    case 'row_full+col_no_group': {
      const rt = (rowErr as { clueTotal: number }).clueTotal;
      return `Row ${r} only needs ${rt} cell${rt > 1 ? 's' : ''}, and this cell doesn't belong to any group in column ${c}!`;
    }

    // Gap + full
    case 'row_gap+col_full': {
      const ct = (colErr as { clueTotal: number }).clueTotal;
      return `Row ${r} has clue ${rowClueStr} — this cell falls in a gap, and column ${c} only needs ${ct} cell${ct > 1 ? 's' : ''}!`;
    }
    case 'row_full+col_gap': {
      const rt = (rowErr as { clueTotal: number }).clueTotal;
      return `Row ${r} only needs ${rt} cell${rt > 1 ? 's' : ''}, and column ${c} has clue ${colClueStr} — this cell falls in a gap!`;
    }

    default:
      return null;
  }
}

/** Describe a single error as a human-readable fragment. */
function describeError(error: CellError, clues: PuzzleClues): string {
  const r = 'row' in error ? error.row + 1 : 0;
  const c = 'col' in error ? error.col + 1 : 0;

  switch (error.type) {
    case 'row_empty':
      return `Row ${r} has no filled cells!`;
    case 'col_empty':
      return `Column ${c} has no filled cells!`;
    case 'row_no_group':
      return `This cell doesn't belong to any group in row ${r}!`;
    case 'col_no_group':
      return `This cell doesn't belong to any group in column ${c}!`;
    case 'row_gap': {
      const clueStr = clues.rows[error.row].join(' ');
      return `Row ${r} has clue ${clueStr} — this cell falls in a gap!`;
    }
    case 'col_gap': {
      const clueStr = clues.cols[error.col].join(' ');
      return `Column ${c} has clue ${clueStr} — this cell falls in a gap!`;
    }
    case 'row_full':
      return `Row ${r} only needs ${error.clueTotal} cell${error.clueTotal > 1 ? 's' : ''}!`;
    case 'col_full':
      return `Column ${c} only needs ${error.clueTotal} cell${error.clueTotal > 1 ? 's' : ''}!`;
  }
}

/** Check whether a completed boolean grid matches the given clues exactly */
export function validate(grid: boolean[][], clues: PuzzleClues): boolean {
  const rows = grid.length;
  if (rows !== clues.rows.length) return false;

  const cols = grid.length > 0 ? grid[0].length : 0;
  if (cols !== clues.cols.length) return false;

  // Check rows
  for (let r = 0; r < rows; r++) {
    const actual = cluesFromLine(grid[r]);
    const expected = clues.rows[r];
    if (!arraysEqual(actual, expected)) return false;
  }

  // Check columns
  for (let c = 0; c < cols; c++) {
    const column = grid.map((row) => row[c]);
    const actual = cluesFromLine(column);
    const expected = clues.cols[c];
    if (!arraysEqual(actual, expected)) return false;
  }

  return true;
}

function arraysEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
